import { Cid, Exam, ExamSearch, LATERALITY, TipoExameFormulario } from '../../../interfaces/exam';
import { ManualMedicine, Medicine, PT_PRESCRIPTION_TYPE, SRMICDUnitByMonth } from '../../../interfaces/medicine';
import {
  MEDICAL_CERTIFICATE_DOCUMENT_TYPE,
  MedicalCertificate,
  MedicalCertificateDTO,
} from '../../../interfaces/medicalCertificate';
import { MedicalReferral } from '../../../interfaces/MedicalReferral';
import { MedicalReport, MedicalReportDTO } from '../../../interfaces/medicalReport';
import { Formula } from '../../../interfaces/formula';
import {
  CreateProtocolCertificateInput,
  CreateProtocolCompoundedFormulaInput,
  CreateProtocolExamInput,
  CreateProtocolMedicationInput,
  CreateProtocolPayload,
  CreateProtocolReferralInput,
  CreateProtocolReportInput,
  ProtocolCertificate,
  ProtocolDetail,
  ProtocolDocumentType,
  ProtocolExam,
  ProtocolExamItem,
  ProtocolMedication,
  ProtocolOrigin,
  ProtocolReferral,
  ProtocolReport,
  StructuredPosology,
} from '../services';
import { Icd } from '../patientService';
import { cleanCertificateReason } from '../../../helpers/medicalCertificateReason';
import {
  generateCompanionDeclarationOrMedicalCertificateText,
  generateDeclarationOfAttendanceText,
} from '../../medical-certificate/helpers';
import { MEDICAL_CERTIFICATE_REASON_OPTIONS } from '../../../contants/medicalCertificateReasons';
import { joinDateWithHourAndMinutes, TIMEZONE } from '../../../helpers/Date';
import { DateTime } from 'luxon';

/** Meses que o formulário LME cobre. */
const LME_MONTHS = 6;

/**
 * A distribuição mensal é utilizável para incluir o medicamento na prescrição.
 *
 * Espelha o que o backend cobra (`É necessario inserir ao menos uma unidade/mes` e
 * `O primeiro mês deve ser preenchido`): o 1º mês precisa existir e ser maior que zero.
 * Vale nas duas direções — guardar meio LME no modelo é tão ruim quanto reidratar um.
 *
 * @param months Meses já normalizados, com a unidade de cada um.
 * @returns `true` quando o 1º mês está preenchido com unidade positiva.
 */
function hasUsableFirstMonth(months: Array<{ month: number; unit: number }>): boolean {
  const firstMonth = months.find(({ month }) => month === 1);
  return Boolean(firstMonth && firstMonth.unit > 0);
}

export function isCatalogMedication(p: ProtocolMedication): boolean {
  return !p.isManual;
}

function parsePositiveQuantity(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/**
 * Reconstrói o formulário LME guardado no modelo.
 *
 * Exige CID e a distribuição mensal com o 1º mês preenchido: é o que a inclusão do
 * medicamento na prescrição valida, e mandar um LME incompleto derruba o medicamento
 * inteiro. Sem isso o medicamento entra sem LME e o médico preenche o formulário.
 *
 * @param p Medicamento do modelo.
 * @returns O LME pronto para a inclusão, ou `undefined` quando o modelo não guardou um válido.
 */
function protocolMedicationToLme(p: ProtocolMedication): SRMICDUnitByMonth | undefined {
  if (!p.isLme || !p.lmeCid) return undefined;

  const months = (p.lmeUnits ?? []).filter(({ month }) => month >= 1 && month <= LME_MONTHS);
  if (!hasUsableFirstMonth(months)) return undefined;

  return { cid: p.lmeCid, unidades: months.map(({ month, unit }) => ({ mes: month, unidade: unit })) };
}

/**
 * Converte um medicamento do modelo no formato da prescrição.
 *
 * O CID específico (`icdCode`) é guardado no modelo apenas como código, mas o backend rejeita
 * a inclusão sem o id do CID (`CID específico do medicamento inválido.`). Por isso ele só é
 * reconstruído quando `cidsByCode` resolveu o código no catálogo; sem resolução, o
 * medicamento entra sem CID e o médico é avisado pelo toast de aplicação.
 *
 * @param p Medicamento do modelo.
 * @param cidsByCode CIDs já resolvidos, indexados por código.
 * @returns O medicamento pronto para ser incluído na prescrição.
 */
export function protocolMedicationToMedicine(
  p: ProtocolMedication,
  cidsByCode?: ReadonlyMap<string, Cid>
): Medicine {
  const resolvedIcd = p.icdCode ? cidsByCode?.get(p.icdCode) : undefined;
  const lmeFromProtocol = protocolMedicationToLme(p);

  return {
    idPrescricaoMedicamento: 0,
    IdMedicamento: Number(p.medicationId) || 0,
    Nome: p.brandName ?? p.activeIngredient ?? '',
    Label: p.brandName ?? p.activeIngredient ?? '',
    Posologia: p.posology ?? '',
    UsoContinuo: p.continuousUse,
    Quantidade: p.quantity ?? parsePositiveQuantity(p.structuredPosology?.quantity),
    isStructuredPosology: Boolean(p.isStructuredPosology) || !!p.structuredPosology,
    LaboratorioPreferencia: p.preferredLaboratoryId ?? 0,
    PermitirTroca: p.allowSubstitution ? 1 : 0,
    FarmaciaPopular: false,
    AtivoAnvisa: false,
    ViaAdministracao: '',
    ImpressaoDuasVias: false,
    Dispensado: false,
    QuantidadeDispensada: null,
    ListaEANS: p.ean ? [p.ean] : [],
    DetalhesDispensa: [],
    Concentracao: '',
    Unidade: '',
    Farmacos: [],
    PrincipiosAtivos: [],
    TipoMedicamento: '',
    TipoMedicamentoComAcentuacao: '',
    FormaFarmaceutica: '',
    FormaFarmaceuticaComAcentuacao: '',
    UsoInterno: p.useInternal,
    Antimicrobiano: p.isAntimicrobial,
    idTipoAntimicrobiano: p.antimicrobialTypeId ?? 0,
    AntimicrobianoDescricao: '',
    Desconto: 0,
    PodeDispensaEletronica: false,
    idPortaria344: 0,
    CodigoATC: '',
    ControleEspecial: false,
    MIP: false,
    TemBeneficio: false,
    LabelFarmaco: '',
    Embalagens: [],
    Embalagem: p.packaging ?? null,
    IdViaAdministracao: 0,
    LME: Boolean(lmeFromProtocol),
    medicamentoLme: lmeFromProtocol,
    ProdutoCorrelato: p.isRelatedProduct,
    medicineIcd: resolvedIcd
      ? {
          icd10Id: resolvedIcd.idCID10,
          icd10: resolvedIcd.Codigo,
          icd: { id: resolvedIcd.idCID10, subcategoria: resolvedIcd.Codigo, descricao: resolvedIcd.Descricao },
        }
      : undefined,
    Beneficios: [],
    QuantidadeUnidadeFormatada: '',
    Ordem: p.sortOrder ?? null,
    EmbalagensEstruturadas: [],
    EmbalagemEstruturada: {} as Medicine['EmbalagemEstruturada'],
    ParanaSemDor: false,
  } as Medicine;
}

export function protocolMedicationToManualMedicine(p: ProtocolMedication): ManualMedicine {
  const name = p.brandName ?? p.activeIngredient ?? '';
  return {
    idPrescricaoMedicamentoManual: null,
    idMedicamentoManual: null,
    Quantidade: p.quantity ?? null,
    UsoContinuo: p.continuousUse,
    UsoInterno: p.useInternal,
    Nome: name,
    Dispensado: false,
    QuantidadeDispensada: null,
    Label: name,
    DetalhesDispensa: [],
    Manual: true,
    Posologia: p.posology ?? '',
    Embalagem: p.packaging ?? null,
    PosologiaEstruturada: p.structuredPosology
      ? ({
          totalTreatment: p.structuredPosology.totalTreatment,
          quantity: p.structuredPosology.quantity,
          unit: p.structuredPosology.unit,
          unitPlural: p.structuredPosology.unitPlural,
          suggestedPackaging: p.structuredPosology.suggestedPackaging,
          suggestedPackagingQuantity: p.structuredPosology.suggestedPackagingQuantity,
        } as unknown as ManualMedicine['PosologiaEstruturada'])
      : undefined,
    LME: p.isLme,
    ProdutoCorrelato: p.isRelatedProduct,
    QuantidadeUnidadeFormatada: '',
    Ordem: p.sortOrder ?? null,
    TipoReceita: (p.prescriptionType as PT_PRESCRIPTION_TYPE | null) ?? PT_PRESCRIPTION_TYPE.SIMPLES,
    Manipulado: p.isCompounded,
  };
}

const LATERALITY_TO_PROTOCOL: Record<LATERALITY, string> = {
  [LATERALITY.LEFT]: 'esquerdo',
  [LATERALITY.RIGHT]: 'direito',
  [LATERALITY.BOTH]: 'bilateral',
};

const PROTOCOL_TO_LATERALITY: Record<string, LATERALITY> = {
  esquerdo: LATERALITY.LEFT,
  direito: LATERALITY.RIGHT,
  bilateral: LATERALITY.BOTH,
};

export function collectProtocolCidCodes(details: ProtocolDetail): string[] {
  const codes = new Set<string>();

  for (const medication of details.medications ?? []) {
    if (medication.icdCode) codes.add(medication.icdCode);
  }

  for (const group of details.exams ?? []) {
    for (const cid of group.prescriptionCids ?? []) {
      if (cid) codes.add(cid);
    }
    for (const item of group.items ?? []) {
      if (item.specificCid) codes.add(item.specificCid);
    }
  }

  for (const certificate of details.certificates ?? []) {
    if (certificate.diagnosisCid) codes.add(certificate.diagnosisCid);
    for (const cid of certificate.secondaryCids ?? []) {
      if (cid) codes.add(cid);
    }
  }

  for (const referral of details.referrals ?? []) {
    if (referral.diagnosisCid) codes.add(referral.diagnosisCid);
  }

  return Array.from(codes);
}

/**
 * CIDs que o modelo guardou para um item de exame.
 *
 * O CID específico do item tem precedência; sem ele valem os CIDs do grupo. Informação que o
 * mapper puro não devolve, mas de que o fluxo de aplicação precisa para avisar o médico
 * quando algum código não pôde ser recuperado do catálogo.
 *
 * @param group Grupo de exames do modelo.
 * @param item Item dentro do grupo.
 * @returns Os códigos de CID aplicáveis ao item, na ordem de precedência.
 */
export function protocolExamItemCidCodes(group: ProtocolExam, item: ProtocolExamItem): string[] {
  if (item.specificCid) return [item.specificCid];
  return (group.prescriptionCids ?? []).filter((code): code is string => Boolean(code));
}

/**
 * Reconhece o CID geral do modelo, quando ele existe.
 *
 * Cobre os dois formatos que o banco guarda: N grupos de 1 item cada, todos com o mesmo
 * `prescriptionCids` (formato que o modal produz), e 1 grupo com N itens migrado do legado. Nos
 * dois casos, "geral" é a união de `prescriptionCids` de todos os grupos ter exatamente 1 código,
 * e nenhum item divergir dele com um `specificCid` próprio.
 *
 * @param groups Grupos de exames do modelo.
 * @returns O código do CID geral, ou `null` quando não há um único CID reconhecível como geral.
 */
export function resolveProtocolGeneralExamCid(groups: ProtocolExam[]): string | null {
  const codes = new Set<string>();
  for (const group of groups) {
    for (const code of group.prescriptionCids ?? []) {
      if (code) codes.add(code);
    }
  }
  if (codes.size !== 1) return null;

  const [code] = Array.from(codes);
  const hasConflictingItem = groups.some((group) =>
    (group.items ?? []).some((item) => item.specificCid && item.specificCid !== code)
  );
  return hasConflictingItem ? null : code;
}

/**
 * Reconhece a observação geral do modelo, quando ela existe.
 *
 * Mesmo raciocínio de {@link resolveProtocolGeneralExamCid}, para `generalObservation` ×
 * `item.observation`.
 *
 * @param groups Grupos de exames do modelo.
 * @returns A observação geral, ou `null` quando não há uma única observação reconhecível como geral.
 */
export function resolveProtocolGeneralExamObservation(groups: ProtocolExam[]): string | null {
  const observations = new Set<string>();
  for (const group of groups) {
    if (group.generalObservation) observations.add(group.generalObservation);
  }
  if (observations.size !== 1) return null;

  const [observation] = Array.from(observations);
  const hasConflictingItem = groups.some((group) =>
    (group.items ?? []).some((item) => item.observation && item.observation !== observation)
  );
  return hasConflictingItem ? null : observation;
}

const CATALOG_FALLBACK = {
  idEzOrder: 0,
  categoria: '',
  idExterno: '',
  sigtap: '',
  referencia: '',
  ReferenciaExterna: null,
  OrigemExame: null,
  examId: '',
  AnestesiaRequerida: false,
} as const;

/**
 * Converte um grupo de exames do modelo na lista de `Exam` da prescrição.
 *
 * Quando o item foi resolvido no catálogo (`examsByTuss`), reaproveitamos a identidade de lá
 * — `idEzOrder`, `sigtap`, `referencia` — porque é o que a prescrição usa para tratar o exame
 * como catalogado. Os dados que o médico personalizou no modelo (descrição, observação,
 * lateralidade, CIDs) continuam vindo do modelo. Sem `Origem` MEDFLOW: exame de modelo deve
 * seguir editável e deletável (MEDFLOW trava a exclusão no card).
 *
 * @param group Grupo de exames do modelo.
 * @param cidsByCode CIDs já resolvidos, indexados por código.
 * @param examsByTuss Exames do catálogo já resolvidos, indexados por código TUSS.
 * @returns Um `Exam` por item do grupo, na mesma ordem.
 */
export function protocolExamToExams(
  group: ProtocolExam,
  cidsByCode?: ReadonlyMap<string, Cid>,
  examsByTuss?: ReadonlyMap<string, ExamSearch>
): Exam[] {
  return (group.items ?? []).map((item) => {
    const tipoFormulario: TipoExameFormulario | null = item.apac
      ? { Chave: 'FORMULARIO_APAC', Nome: 'APAC', idTipoExameFormulario: 1 }
      : item.bpaI
        ? { Chave: 'FORMULARIO_BPA-I', Nome: 'BPA-I', idTipoExameFormulario: 2 }
        : null;

    const lateralidade = item.laterality ? (PROTOCOL_TO_LATERALITY[item.laterality] ?? null) : null;

    // Só o CID específico do item é reidratado no exame individual. O CID geral do grupo
    // (`prescriptionCids`) vai para o bloco geral da prescrição via `resolveProtocolGeneralExamCid`
    // + `examStore`; reidratá-lo aqui duplicaria no PDF o CID que já sai no bloco geral.
    const structuredCids = (item.specificCid ? [item.specificCid] : [])
      .map((code) => cidsByCode?.get(code))
      .filter((cid): cid is Cid => Boolean(cid));

    const catalog = item.tussCode ? examsByTuss?.get(item.tussCode) : undefined;

    return {
      ...CATALOG_FALLBACK,
      ...(catalog ?? {}),
      LadoRequerido: catalog?.LadoRequerido ?? Boolean(lateralidade),
      Nome: item.description ?? catalog?.Nome ?? '',
      Label: item.description ?? catalog?.Label ?? '',
      CodigoTUSS: item.tussCode ?? catalog?.CodigoTUSS ?? '',
      AplicarAnestesia: false,
      Instrucoes: item.observation ?? '',
      Lateralidade: lateralidade,
      CID10Estruturado: structuredCids,
      tipoFormulario,
      QuantidadeSessoes: item.sessionCount ?? null,
      // CidEspecifico: Boolean(specificCid),
      CidEspecifico: structuredCids.length > 0,
      ObservacaoEspecifica: Boolean(item.observation),
    } as unknown as Exam;
  });
}

function cidToIcd(cid: Cid): Icd {
  return { id: cid.idCID10, subcategory: cid.Codigo, description: cid.Descricao };
}

/**
 * Converte o atestado do modelo no DTO aceito pela prescrição.
 *
 * `consentText` e `hasConsent` só são enviados quando há CID resolvido: o consentimento é
 * sobre revelar o diagnóstico, então sem CID ele não tem a que se referir.
 *
 * O modelo não guarda datas — elas eram do atendimento em que o atestado foi criado. Aplicar o
 * modelo equivale a abrir o formulário com um atestado novo: emissão, início e término caem
 * todos na meia-noite de hoje, que é como o formulário codifica "sem horário de entrada nem de
 * saída". Os dias de repouso seguem no DTO, mas não esticam o término — quem empurra a data de
 * saída é o médico, no campo de data, e o formulário nunca a deriva do período.
 *
 * Um término adiantado nasceria com horários que o formulário não sabe limpar ao reabrir: ele só
 * apaga o par quando entrada e saída marcam meia-noite. O atestado voltaria exibindo um horário
 * de entrada que o médico nunca digitou e, ao encurtar a saída para o mesmo dia, esbarraria na
 * regra de que a saída tem que ser maior que a entrada.
 *
 * Declarações têm o corpo inteiro gerado por template, com data e paciente embutidos, então o
 * texto guardado no modelo é descartado e regerado a partir do atendimento atual — mesmo
 * descarte que o fluxo manual faz ao mudar a data. Sem isso a declaração sairia com a data e o
 * nome do paciente do atendimento em que o modelo foi montado.
 *
 * @param c Atestado guardado no modelo.
 * @param cidsByCode CIDs já resolvidos, indexados por código.
 * @param patientName Paciente da prescrição em que o modelo está sendo aplicado.
 * @returns O DTO de criação do atestado.
 */
export function protocolCertificateToDTO(
  c: ProtocolCertificate,
  cidsByCode?: ReadonlyMap<string, Cid>,
  patientName?: string
): MedicalCertificateDTO {
  const now = DateTime.now().setZone(TIMEZONE);
  const todayLabel = now.toFormat('dd/MM/yyyy');
  const today = joinDateWithHourAndMinutes(todayLabel);
  const type =
    c.type && (Object.values(MEDICAL_CERTIFICATE_DOCUMENT_TYPE) as string[]).includes(c.type)
      ? (c.type as MEDICAL_CERTIFICATE_DOCUMENT_TYPE)
      : MEDICAL_CERTIFICATE_DOCUMENT_TYPE.MEDICAL_CERTIFICATE;
  const isDeclaration =
    type === MEDICAL_CERTIFICATE_DOCUMENT_TYPE.DECLARATION_OF_ATTENDANCE ||
    type === MEDICAL_CERTIFICATE_DOCUMENT_TYPE.DECLARATION_OF_COMPANION ||
    type === MEDICAL_CERTIFICATE_DOCUMENT_TYPE.COMPANION_MEDICAL_CERTIFICATE;

  const resolvedIcd = c.diagnosisCid ? cidsByCode?.get(c.diagnosisCid) : undefined;
  const secondaryIcds = (c.secondaryCids ?? [])
    .map((code) => cidsByCode?.get(code))
    .filter((cid): cid is Cid => Boolean(cid))
    .map(cidToIcd);
  const icd = resolvedIcd ? cidToIcd(resolvedIcd) : null;
  const hasIcd = Boolean(icd) || secondaryIcds.length > 0;

  const baseText = c.reason ? cleanCertificateReason(c.reason, c.isFreeText) : '';
  const needsExitDate =
    !c.isFreeText &&
    (baseText === MEDICAL_CERTIFICATE_REASON_OPTIONS.ORIENTED_TO_REST_FOR_PERIOD ||
      baseText === MEDICAL_CERTIFICATE_REASON_OPTIONS.ORIENTED_TO_REST_TODAY);

  const declarationParams = {
    startHour: '',
    endHour: '',
    entranceDate: todayLabel,
    exitDate: todayLabel,
    patientName: patientName ?? '',
  };
  const declarationText = !isDeclaration
    ? null
    : type === MEDICAL_CERTIFICATE_DOCUMENT_TYPE.DECLARATION_OF_ATTENDANCE
    ? generateDeclarationOfAttendanceText(declarationParams)
    : generateCompanionDeclarationOrMedicalCertificateText(declarationParams);

  return {
    icd,
    isFreeText: c.isFreeText,
    medicalCertificateDate: today,
    startDate: today,
    endDate: today,
    medicalCertificateId: 0,
    period: c.period ?? '',
    observation: c.observation ?? '',
    text: declarationText ?? (needsExitDate ? baseText.replace('hoje', `hoje (${todayLabel})`) : baseText),
    consentText: hasIcd ? c.consentText ?? '' : '',
    restPeriodType: c.restPeriodType ?? '',
    hasConsent: hasIcd && c.hasConsent,
    isDeclarationOfAttendance: type === MEDICAL_CERTIFICATE_DOCUMENT_TYPE.DECLARATION_OF_ATTENDANCE,
    type,
    secondaryIcds,
  };
}

export function protocolReferralToMedicalReferral(
  r: ProtocolReferral,
  cidsByCode?: ReadonlyMap<string, Cid>
): MedicalReferral {
  const resolvedCid = r.diagnosisCid ? cidsByCode?.get(r.diagnosisCid) : undefined;
  return {
    idEncaminhamentoPrescricao: 0,
    DataEncaminhamento: DateTime.now().setZone(TIMEZONE).startOf('day').toJSDate(),
    Motivo: r.reason ?? '',
    HistoriaClinica: r.clinicalHistory ?? '',
    Conduta: r.conduct ?? '',
    Especialidade: {
      IdEspecialidade: r.specialtyId ?? 0,
      NomeProfissional: r.specialty ?? '',
      Nome: r.specialty ?? '',
    },
    CID10: resolvedCid
      ? {
          IdSubCategoria: resolvedCid.idCID10,
          SubCategoria: resolvedCid.Codigo,
          Descricao: resolvedCid.Descricao,
        }
      : null,
    tipoParecer: r.reportType ?? '',
  };
}

export function protocolReportToDTO(r: ProtocolReport): MedicalReportDTO {
  return {
    text: r.description ?? '',
    title: r.documentType ?? '',
    medicalReportDate: DateTime.now().setZone(TIMEZONE).toFormat("yyyy-MM-dd'T'HH:mm:ss"),
    medicalReportId: 0,
  };
}

function buildStructuredPosologyFromMedicine(m: Medicine): StructuredPosology | null {
  if (!m.isStructuredPosology) return null;

  const legacy = m.PosologiaEstruturada;
  if (legacy) {
    return {
      totalTreatment: legacy.TotalTratamento ?? '',
      quantity: legacy.Quantidade ?? '',
      unit: legacy.Unidade ?? '',
      unitPlural: legacy.UnidadePlural ?? '',
      suggestedPackaging: legacy.EmbalagemSugerida ?? 0,
      suggestedPackagingQuantity: legacy.QuantidadeEmbalagemSugerida ?? 0,
    };
  }

  const pack = m.EmbalagemEstruturada;
  return {
    totalTreatment: m.Quantidade != null ? String(m.Quantidade) : '',
    quantity: m.Quantidade != null ? String(m.Quantidade) : '',
    unit: m.Unidade ?? '',
    unitPlural: m.Unidade ?? '',
    suggestedPackaging: pack?.VolumeEmbalagem ?? 0,
    suggestedPackagingQuantity: pack?.QuantidadeEmbalagens ?? 0,
  };
}

/** Campos de LME do input de criação do medicamento no modelo. */
type LmeInputFields = Pick<
  CreateProtocolMedicationInput,
  'isLme' | 'lmeCid' | 'lmeUnits' | 'lmeCnes' | 'lmeDoctorCns' | 'lmeAnamnesis' | 'lmeInformedConsent'
>;

/**
 * Guarda o LME do medicamento como o modelo o entende: CID e distribuição mensal.
 *
 * CNES, CNS, anamnese e consentimento ficam de fora de propósito — os dois primeiros o
 * serviço de prescrição deriva do prescritor e do estabelecimento na inclusão, e os dois
 * últimos são do atendimento daquele paciente. O total do tratamento é derivado das unidades
 * no backend, então também não sai daqui.
 *
 * Paraná Sem Dor usa a mesma estrutura mas é outro formulário, com CID fixo e emissão
 * própria; sem campo para distinguir os dois no modelo, ele não é preservado — reidratar
 * como LME comum geraria o documento errado.
 *
 * @param m Medicamento de catálogo da prescrição.
 * @returns Os campos de LME do input, ou o LME desligado quando não há formulário.
 */
function lmeFieldsFromMedicine(m: Medicine): LmeInputFields {
  const lme = m.medicamentoLme;
  const withoutLme: LmeInputFields = {
    isLme: false,
    lmeCid: null,
    lmeUnits: null,
    lmeCnes: null,
    lmeDoctorCns: null,
    lmeAnamnesis: null,
    lmeInformedConsent: false,
  };

  if (!lme || lme.paranaSemDor || !lme.cid) return withoutLme;

  // Mês repetido faz o backend recusar o modelo INTEIRO; o formulário tem seis campos fixos,
  // então basta ficar com a última ocorrência de cada mês em vez de arriscar o 400.
  const unitsByMonth = new Map<number, number>();
  for (const { mes, unidade } of lme.unidades ?? []) {
    if (!Number.isFinite(unidade) || mes < 1 || mes > LME_MONTHS) continue;
    unitsByMonth.set(mes, unidade);
  }

  const units = Array.from(unitsByMonth, ([month, unit]) => ({ month, unit })).sort(
    (a, b) => a.month - b.month
  );

  if (!hasUsableFirstMonth(units)) return withoutLme;

  return { ...withoutLme, isLme: true, lmeCid: lme.cid, lmeUnits: units };
}

/**
 * Converte um medicamento da prescrição no input de criação do modelo.
 *
 * `quantity` vira `null` quando não é positiva, porque o backend exige `Min(1)` — 0 não é
 * "sem quantidade", é inválido.
 *
 * @param m Medicamento de catálogo da prescrição.
 * @returns O input de criação do medicamento no modelo.
 */
export function medicineToProtocolMedicationInput(m: Medicine): CreateProtocolMedicationInput {
  const structuredPosology = buildStructuredPosologyFromMedicine(m);
  return {
    isManual: false,
    ean: m.ListaEANS?.[0] != null ? String(m.ListaEANS[0]) : null,
    medicationId: m.IdMedicamento ? String(m.IdMedicamento) : null,
    brandName: m.Nome ?? m.Label ?? null,
    activeIngredient: m.LabelFarmaco ?? null,
    posology: m.Posologia ?? null,
    continuousUse: Boolean(m.UsoContinuo),
    structuredPosology,
    ...lmeFieldsFromMedicine(m),
    quantity: m.Quantidade ? m.Quantidade : null,
    packaging: m.Embalagem != null ? String(m.Embalagem) : null,
    prescriptionType: null,
    isCompounded: false,
    allowSubstitution: m.PermitirTroca === 1,
    preferredLaboratoryId: m.LaboratorioPreferencia || null,
    useInternal: Boolean(m.UsoInterno),
    isStructuredPosology: Boolean(m.isStructuredPosology),
    isAntimicrobial: Boolean(m.Antimicrobiano),
    antimicrobialTypeId: m.idTipoAntimicrobiano || null,
    isRelatedProduct: Boolean(m.ProdutoCorrelato),
    // Só o CID específico do medicamento (C5). O CID do LME vai em `lmeCid`: são campos
    // diferentes, e reidratar o do LME como específico marca um CID que a edição não remove.
    icdCode: m.medicineIcd?.icd10 ?? null,
    sortOrder: m.Ordem ?? undefined,
  };
}

export function manualMedicineToProtocolMedicationInput(m: ManualMedicine): CreateProtocolMedicationInput {
  return {
    isManual: true,
    ean: null,
    medicationId: null,
    brandName: m.Nome ?? m.Label ?? null,
    activeIngredient: null,
    posology: m.Posologia ?? null,
    continuousUse: Boolean(m.UsoContinuo),
    structuredPosology: null,
    isLme: false,
    lmeCid: null,
    lmeUnits: null,
    lmeCnes: null,
    lmeDoctorCns: null,
    lmeAnamnesis: null,
    lmeInformedConsent: false,
    quantity: m.Quantidade ? m.Quantidade : null,
    packaging: m.Embalagem != null ? String(m.Embalagem) : null,
    prescriptionType: m.TipoReceita ?? null,
    isCompounded: Boolean(m.Manipulado),
    allowSubstitution: false,
    useInternal: Boolean(m.UsoInterno),
    isStructuredPosology: false,
    isAntimicrobial: false,
    antimicrobialTypeId: null,
    isRelatedProduct: Boolean(m.ProdutoCorrelato),
    icdCode: null,
    sortOrder: m.Ordem ?? undefined,
  };
}

/**
 * Converte um exame da prescrição no input de criação do exame no modelo.
 *
 * O CID geral chega pronto em `e.CID10Estruturado` — escolher CID geral na prescrição já grava
 * esse valor em cada exame não específico (`IncludedExams.onChangeGeneralIcd`). A observação
 * geral **não** segue o mesmo caminho: `onChangeObservation` só atualiza o estado da prescrição
 * (`examStore.generalExamObservation`), nunca o `Instrucoes` de cada exame — por isso ela precisa
 * chegar aqui por parâmetro; usar `e.Instrucoes` sempre resultaria em `generalObservation` vazio.
 *
 * @param e Exame da prescrição.
 * @param generalObservation Observação geral da prescrição no momento de salvar o modelo.
 * @returns O input de criação do exame no modelo.
 */
export function examToProtocolExamInput(
  e: Exam,
  generalObservation: string | null = null
): CreateProtocolExamInput {
  const isCidSpecific = Boolean(e.CidEspecifico);
  const isObservationSpecific = Boolean(e.ObservacaoEspecifica);
  const cids = (e.CID10Estruturado ?? []).map((c) => c.Codigo).filter((c): c is string => !!c);
  const firstCid = cids[0] ?? null;
  return {
    prescriptionCids: !isCidSpecific && cids.length > 0 ? cids : null,
    generalObservation: !isObservationSpecific ? generalObservation || null : null,
    sortOrder: undefined,
    items: [
      {
        tussCode: e.CodigoTUSS ?? null,
        description: e.Nome ?? e.Label ?? null,
        specificCid: isCidSpecific ? firstCid : null,
        observation: isObservationSpecific ? e.Instrucoes ?? null : null,
        laterality: e.Lateralidade ? LATERALITY_TO_PROTOCOL[e.Lateralidade] : null,
        sessionCount: e.QuantidadeSessoes ?? null,
        apac: e.tipoFormulario?.Chave === 'FORMULARIO_APAC',
        bpaI: e.tipoFormulario?.Chave === 'FORMULARIO_BPA-I',
        sortOrder: undefined,
      },
    ],
  };
}

export function medicalCertificateToProtocolCertificateInput(
  c: MedicalCertificate
): CreateProtocolCertificateInput {
  return {
    reason: c.Texto ? cleanCertificateReason(c.Texto, c.CampoLivre) : null,
    observation: c.Observacao ?? null,
    type: c.Tipo ?? null,
    period: c.Periodo ?? null,
    restPeriodType: c.TipoPeriodo ?? null,
    diagnosisCid: c.CID10Estruturado?.[0]?.Codigo ?? null,
    secondaryCids: (c.CID10EstruturadoSecundarios ?? []).map((cid) => cid.Codigo).filter(Boolean),
    consentText: c.TextoConsentimento || null,
    hasConsent: Boolean(c.ConsentimentoCID),
    isFreeText: c.CampoLivre,
    sortOrder: undefined,
  };
}

export function medicalReferralToProtocolReferralInput(r: MedicalReferral): CreateProtocolReferralInput {
  return {
    specialty: r.Especialidade?.Nome ?? null,
    specialtyId: r.Especialidade?.IdEspecialidade || null,
    diagnosisCid: r.CID10?.SubCategoria ?? null,
    reason: r.Motivo ?? null,
    clinicalHistory: r.HistoriaClinica ?? null,
    conduct: r.Conduta ?? null,
    reportType: r.tipoParecer ?? null,
    sortOrder: undefined,
  };
}

export function medicalReportToProtocolReportInput(r: MedicalReport): CreateProtocolReportInput {
  return {
    documentType: r.Titulo ?? null,
    description: r.Texto ?? null,
    sortOrder: undefined,
  };
}

export function formulaToProtocolCompoundedFormulaInput(
  f: Formula
): CreateProtocolCompoundedFormulaInput {
  return {
    formulaId: f.id,
    sortOrder: undefined,
  };
}

/**
 * Escolhe o `documentType` do modelo a partir da contagem de itens.
 *
 * O contrato exige um único tipo por modelo; em caso de empate vale a prioridade
 * medication > exam > certificate > referral > report.
 *
 * @param counts Quantidade de itens de cada tipo.
 * @param flags Sinalizadores que refinam o tipo vencedor (ex.: medicamento controlado).
 * @returns O tipo de documento do modelo.
 */
export function inferDocumentType(
  counts: {
    medications: number;
    exams: number;
    certificates: number;
    referrals: number;
    reports: number;
  },
  flags: { hasControlledMedication?: boolean } = {}
): ProtocolDocumentType {
  const order: Array<[ProtocolDocumentType, number]> = [
    ['medication', counts.medications],
    ['exam', counts.exams],
    ['certificate', counts.certificates],
    ['referral', counts.referrals],
    ['report', counts.reports],
  ];
  const winner = order
    .map(([type, count], idx) => ({ type, count, idx }))
    .sort((a, b) => b.count - a.count || a.idx - b.idx)[0]; // sort estável: empate mantém a ordem original

  if (winner.type === 'medication' && flags.hasControlledMedication) {
    return 'controlled_medication';
  }
  return winner.type;
}

function isMedicineControlled(m: Medicine): boolean {
  if (m.ControleEspecial) return true;
  if (m.idPortaria344 && m.idPortaria344 !== 0 && m.idPortaria344 !== '0') return true;
  return false;
}

const CONTROLLED_PRESCRIPTION_TYPES: ReadonlySet<PT_PRESCRIPTION_TYPE> = new Set([
  PT_PRESCRIPTION_TYPE.CONTROLADO,
  PT_PRESCRIPTION_TYPE.MANIPULADO_CONTROLADO,
]);

function isManualMedicineControlled(m: ManualMedicine): boolean {
  if (!m.TipoReceita) return false;
  return CONTROLLED_PRESCRIPTION_TYPES.has(m.TipoReceita);
}

/**
 * Fórmula manipulada de receita controlada.
 *
 * Entra na detecção porque `buildCreateProtocolPayload` conta fórmula no bucket `medication`
 * do `documentType` — sem isto, um modelo composto só por fórmula manipulada controlada
 * sairia classificado como `medication` em vez de `controlled_medication`.
 *
 * @param f Fórmula selecionada na prescrição.
 * @returns Verdadeiro quando o tipo de receita da fórmula é controlado.
 */
function isFormulaControlled(f: Formula): boolean {
  if (!f.prescriptionType) return false;
  return CONTROLLED_PRESCRIPTION_TYPES.has(f.prescriptionType as PT_PRESCRIPTION_TYPE);
}

export interface PrescriptionItemsForModel {
  medicines: Medicine[];
  manualMedicines: ManualMedicine[];
  exams: Exam[];
  certificates: MedicalCertificate[];
  referrals: MedicalReferral[];
  reports: MedicalReport[];
  formulas: Formula[];
}

/**
 * Monta o payload de criação de modelo a partir dos itens escolhidos na prescrição.
 *
 * `prescriptionId` é obrigatório porque é o que permite ao BFF resolver o subparceiro do
 * prescritor — sem ele o modelo institucional nasce no nível do parceiro pai e um
 * administrador de subparceiro é barrado com 403.
 *
 * @param args Nome, identidade do prescritor, prescrição em contexto, origem e itens.
 * @returns O payload de `POST /protocols`.
 */
export function buildCreateProtocolPayload(args: {
  name: string;
  doctorId: number;
  partnerId: number;
  prescriptionId: number;
  publicationStatus?: 'draft' | 'published' | 'archived';
  origin?: ProtocolOrigin;
  /** Observação da prescrição, quando o prescritor optou por guardá-la no modelo. */
  observation?: string | null;
  /**
   * Observação geral dos exames da prescrição (`examStore.generalExamObservation`) — diferente de
   * `observation` acima. Precisa vir por parâmetro porque nenhum `Exam` carrega esse valor (ver
   * {@link examToProtocolExamInput}).
   */
  examGeneralObservation?: string | null;
  items: PrescriptionItemsForModel;
}): CreateProtocolPayload {
  const { items } = args;
  const medications: CreateProtocolMedicationInput[] = [
    ...items.medicines.map(medicineToProtocolMedicationInput),
    ...items.manualMedicines.map(manualMedicineToProtocolMedicationInput),
  ];
  const exams = items.exams.map((e) => examToProtocolExamInput(e, args.examGeneralObservation ?? null));
  const certificates = items.certificates.map(medicalCertificateToProtocolCertificateInput);
  const referrals = items.referrals.map(medicalReferralToProtocolReferralInput);
  const reports = items.reports.map(medicalReportToProtocolReportInput);
  const compoundedFormulas = items.formulas.map(formulaToProtocolCompoundedFormulaInput);

  const hasControlledMedication =
    items.medicines.some(isMedicineControlled) ||
    items.manualMedicines.some(isManualMedicineControlled) ||
    items.formulas.some(isFormulaControlled);

  const documentType = inferDocumentType(
    {
      medications: medications.length + compoundedFormulas.length,
      exams: exams.length,
      certificates: certificates.length,
      referrals: referrals.length,
      reports: reports.length,
    },
    { hasControlledMedication }
  );

  return {
    doctorId: args.doctorId,
    partnerId: args.partnerId,
    prescriptionId: args.prescriptionId,
    name: args.name,
    documentType,
    publicationStatus: args.publicationStatus ?? 'published',
    ...(args.origin && { origin: args.origin }),
    ...(args.observation ? { observation: args.observation } : {}),
    medications: medications.length > 0 ? medications : undefined,
    exams: exams.length > 0 ? exams : undefined,
    certificates: certificates.length > 0 ? certificates : undefined,
    referrals: referrals.length > 0 ? referrals : undefined,
    reports: reports.length > 0 ? reports : undefined,
    compoundedFormulas: compoundedFormulas.length > 0 ? compoundedFormulas : undefined,
  };
}
